import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { translations } from "@/lib/content";
import { api } from "@/lib/api";
import { lookup, interpolate, pickLocalized } from "@/lib/i18n";
// Import RELATIF volontaire (module frère) : l'alias "@/" n'est pas mappé côté Jest,
// et les suites existantes contournent cela en mockant chaque "@/lib/*" en virtual.
// Un chemin relatif se résout nativement — pas de mock à ajouter, pas de config à
// toucher, et la vraie fusion (et non un mock) reste sous test.
import { BASE_LOCALE, resolveLocale } from "./localeMerge";
import { readStoredLang } from "./storedLang";

// The PUBLIC SITE's resolved copy is NOT exposed here. It needs the tenant record (the
// contact e-mail falls back to the practice's inbox), and this provider sits above
// TenantProvider — which tenant/TenantSite.jsx re-nests per site. Everything that renders
// public copy calls `useSiteCopy()` (lib/useSiteCopy.js), the one place that owns all three
// inputs; this context owns the language half only.
const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: translations.en,
  overrides: {},
  override: (key) => "",
});

export const LanguageProvider = ({ children }) => {
  // Normalised on the way in (`storedLang`): "fr_CA" is French, not an unknown code.
  const [lang, setLang] = useState(readStoredLang);
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (typeof window !== "undefined" && lang) {
      localStorage.setItem("eh_lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        setOverrides(data || {});
      } catch (e) {
        // /content not available (404) on SaaS root — keep defaults silently
        if (process.env.NODE_ENV !== "production") console.debug("[i18n] no tenant content overrides:", e?.message);
      }
    })();
  }, []);

  // Return override value if non-empty in current lang, else empty string (caller falls back).
  // EN ne retombe plus sur value_fr : un override rédigé uniquement en français laisse
  // les visiteurs EN sur le texte anglais par défaut du dictionnaire (choix produit
  // du passage EN-défaut), au lieu de leur montrer du français.
  const override = useCallback(
    (key) => {
      const o = overrides?.[key];
      if (!o) return "";
      if (lang === "en") return o.value_en || "";
      if (lang === "fr") return o.value_fr || "";
      return pickLocalized(o, "value", lang);
    },
    [overrides, lang],
  );

  const effectiveLang = lang || BASE_LOCALE;
  const t = useMemo(
    () => resolveLocale(translations, effectiveLang),
    [effectiveLang],
  );
  const value = { lang: effectiveLang, setLang, t, overrides, override, availableLangs: ["en", "fr", "es"] };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

/**
 * Hook namespaced i18n. Renvoie `t(key, vars?, fallback?)`.
 * Ex: const t = useT("saas"); t("hero.titleA")
 */
export const useT = (namespace) => {
  const { lang } = useLanguage();
  return useMemo(
    () => (key, vars, fb) => interpolate(lookup(namespace, key, lang, fb), vars),
    [namespace, lang],
  );
};

/**
 * Hook pour les champs de données localisés (name_fr/name_en/…, value_fr/…).
 * Ex: const loc = useLocalized(); loc(product, "name")
 */
export const useLocalized = () => {
  const { lang } = useLanguage();
  return useMemo(() => (obj, field) => pickLocalized(obj, field, lang), [lang]);
};
